import { useState } from 'react';
import { callApi } from './api';
import { buildShutchoWordBlob } from './shutchoExport';
import { downloadBlob } from './wordExport';
import ShutchoPreview from './ShutchoPreview';

const REIWA_START = new Date(2019, 4, 1); // 令和元年5月1日。それより前は西暦のまま扱う。
function eraDateLabel(d) {
  const y = d.getFullYear();
  const m = d.getMonth() + 1;
  const day = d.getDate();
  if (d < REIWA_START) return `${y}年${m}月${day}日`;
  const n = y - 2018;
  return `令和${n === 1 ? '元' : n}年${m}月${day}日`;
}
function todayEraDate() {
  return eraDateLabel(new Date());
}

/** 各種報告書：出張報告書用の画面。出張のメモをAIでまとめ、様式どおりのWordを出力する。 */
export default function ShutchoScreen({ onBack }) {
  const [fields, setFields] = useState({
    submitDate: todayEraDate(), orderDate: todayEraDate(), role: '総務', name: '本山将也', other: '',
  });
  const [inputText, setInputText] = useState('');
  const [resultText, setResultText] = useState('');
  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState('');

  function updateField(key, value) {
    setFields((prev) => ({ ...prev, [key]: value }));
  }

  async function handleGenerate() {
    if (!inputText.trim() || generating) return;
    setGenerating(true);
    setGenerateError('');
    try {
      const data = await callApi('shutcho_generate', { inputText: inputText.trim() });
      setResultText(data.text);
    } catch (err) {
      setGenerateError(err.message);
    } finally {
      setGenerating(false);
    }
  }

  async function handleDownload() {
    if (downloading) return;
    setDownloading(true);
    setDownloadError('');
    try {
      const blob = await buildShutchoWordBlob(fields, resultText);
      downloadBlob(blob, `出張報告書_${fields.name || '無題'}.docx`);
    } catch (err) {
      setDownloadError('Wordファイルの作成に失敗しました。');
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div className="workspace is-wide">
      <div className="workspace-head reveal r1">
        <button className="back-link" type="button" onClick={onBack}>← 各種報告書へ戻る</button>
        <h1 className="workspace-title">出張報告書</h1>
      </div>

      <div className="notice reveal r2">
        利用者様・ご家族のお名前など、個人情報は入力しないでください。
      </div>

      <div className="gen-stack">
        <div className="editor-card reveal r2">
          <h2 className="admin-dept-title">出張のメモを貼り付け</h2>
          <textarea
            className="editor-textarea"
            placeholder="出張の目的、行った内容、得られた成果などを自由に書いてください（箇条書きでも構いません）"
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            disabled={generating}
          />
          <div className="admin-save-row">
            <button
              className="enter admin-save-btn" type="button"
              disabled={!inputText.trim() || generating}
              onClick={handleGenerate}
            >
              {generating ? 'まとめています…' : 'AIで成果欄にまとめる'}
            </button>
          </div>
          {generateError && <p className="pin-error" role="alert">{generateError}</p>}
        </div>

        <div className="gen-layout reveal r3">
          <div className="result-card">
            <div className="result-head">
              <h2 className="result-title">入力フォーム</h2>
            </div>
            <div className="word-form-grid">
              <label className="word-form-field">
                <span>提出日</span>
                <input type="text" value={fields.submitDate} onChange={(e) => updateField('submitDate', e.target.value)} />
              </label>
              <label className="word-form-field">
                <span>出張命令書の日付</span>
                <input type="text" value={fields.orderDate} onChange={(e) => updateField('orderDate', e.target.value)} />
              </label>
              <label className="word-form-field">
                <span>役職</span>
                <input type="text" value={fields.role} onChange={(e) => updateField('role', e.target.value)} />
              </label>
              <label className="word-form-field">
                <span>氏名</span>
                <input type="text" value={fields.name} onChange={(e) => updateField('name', e.target.value)} />
              </label>
            </div>
            <label className="editor-label" htmlFor="shutcho-result">成果</label>
            <textarea
              id="shutcho-result"
              className="editor-textarea"
              placeholder="AIでまとめた成果がここに表示されます。直接編集もできます。"
              value={resultText}
              onChange={(e) => setResultText(e.target.value)}
            />
            <label className="editor-label" htmlFor="shutcho-other">その他</label>
            <textarea
              id="shutcho-other"
              className="editor-textarea"
              placeholder="（該当なし）"
              value={fields.other}
              onChange={(e) => updateField('other', e.target.value)}
            />
          </div>

          <div className="preview-card">
            <div className="preview-card-head">
              <h2 className="result-title">Wordプレビュー</h2>
              <button className="enter word-download-btn" type="button" disabled={downloading} onClick={handleDownload}>
                {downloading ? '作成中…' : 'Wordでダウンロード'}
              </button>
            </div>
            {downloadError && <p className="pin-error" role="alert">{downloadError}</p>}
            <ShutchoPreview
              fields={fields}
              onFieldChange={updateField}
              resultText={resultText}
              onResultChange={setResultText}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
